'use client';
import { ArrowDownNarrowWide, ArrowDownUp, ArrowDownWideNarrow } from 'lucide-react';
import { useTranslations } from 'next-intl';
import { useCallback } from 'react';
import FilterSortContainer from '@//containers/FilterSort';
import type { ElasticSearchResponseDocumentListSortOption } from '@//ElasticSearch';
import useUpdateVehiclesSearchUrl from '@//utils/vehicles-search/useUpdateVehiclesSearchUrl';
import useVehicleSearchParams from '@//utils/vehicles-search/useVehicleSearchParams';

const DEFAULT_SORT = 'itemSortDesc';

const extractSortValue = (query: string) => {
  const params = new URLSearchParams(query);
  const sort = params.get('sort') || '';
  return sort === DEFAULT_SORT ? '' : sort;
};

const getSortIcon = (sortValue: string | undefined) => {
  switch (sortValue) {
    case '':
    case 'priceDefaultDesc':
    case 'modelYearDesc':
    case 'mileageDesc':
      return <ArrowDownWideNarrow aria-hidden="true" />;

    case 'priceDefaultAsc':
    case 'modelYearAsc':
    case 'mileageAsc':
      return <ArrowDownNarrowWide aria-hidden="true" />;
    default:
      return <ArrowDownUp aria-hidden="true" />;
  }
};

interface FilterSortProps {
  rootUrl: string;
  sortOptions?: ElasticSearchResponseDocumentListSortOption[];
}

const FilterSort = ({ rootUrl, sortOptions = [] }: FilterSortProps) => {
  const t = useTranslations('pages.search.sort');
  const updateUrl = useUpdateVehiclesSearchUrl(rootUrl);
  const searchParams = useVehicleSearchParams(rootUrl);

  const currentSort = searchParams.get('sort') || '';

  const handleSortChange = useCallback(
    (value: string) => {
      const nextParams = new URLSearchParams(searchParams);
      if (value) {
        nextParams.set('sort', value);
      } else {
        nextParams.delete('sort');
      }
      updateUrl(nextParams);
    },
    [searchParams, updateUrl],
  );

  return (
    <FilterSortContainer
      sortOptions={sortOptions}
      currentSort={currentSort}
      defaultSort=""
      extractSortValue={extractSortValue}
      onSortChange={handleSortChange}
      getSortIcon={getSortIcon}
      buttonTitle={t('button.title')}
      buttonAriaLabel={t('button.ariaLabel', { currentSort: '{currentSort}' })}
      buttonPrefix={t('button.prefix')}
      buttonUnknown={t('button.unknown')}
      menuAriaLabel={t('menu.ariaLabel')}
      menuOptionTitle={t('menu.optionTitle', { option: '{option}' })}
    />
  );
};

export default FilterSort;
